/**
 * Chép khối thông số máy ảnh (EXIF) từ ảnh gốc sang bản nén lại (CEO chốt 2a,
 * 24/10/2026).
 *
 * Vì sao cần: "Tối ưu ảnh" giải mã ảnh rồi mã hoá lại bằng MozJPEG — bộ mã hoá
 * chỉ nhận điểm ảnh nên bản nén ra KHÔNG còn EXIF. Thanh ISO / tốc độ / khẩu /
 * tiêu cự dưới khung soi của thợ chụp (QĐ 0390) đọc đúng khối này ở máy chủ
 * (`imaging.ReadCameraExif`), nên mất EXIF là thanh trống.
 *
 * Chỉ VÁ TẠI CHỖ vài giá trị, không dựng lại cấu trúc: MakerNote của từng hãng
 * máy trỏ bằng độ lệch tuyệt đối trong khối TIFF, dời một byte là hỏng. Các ô
 * được vá:
 * - Orientation (IFD0) → 1 khi trình duyệt ĐÃ xoay điểm ảnh lúc giải mã; giữ
 *   nguyên cờ thì máy chủ / phần mềm xem ảnh xoay thêm lần nữa.
 * - ColorSpace (Exif IFD) → 1 (sRGB): khung vẽ đã đổi màu về sRGB.
 * - InteroperabilityIndex "R03" (Adobe RGB kiểu DCF) → "R98" (sRGB), cùng lý do.
 * - PixelXDimension / PixelYDimension → kích thước bản nén.
 *
 * Mọi chỗ bất thường (độ lệch tràn, IFD vòng lặp, kiểu lạ) → trả `null` = bỏ
 * EXIF, tức đúng như trước khi có tệp này. Không bao giờ ném lỗi: nén chỉ là tối
 * ưu, không được làm hỏng lượt tải.
 */

const SOI = 0xd8;
const EOI = 0xd9;
const SOS = 0xda;
const APP0 = 0xe0;
const APP1 = 0xe1;
const EXIF_HEADER = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00]; // "Exif\0\0"
/** Độ lệch của khối TIFF trong đoạn APP1: FF E1 · 2 byte độ dài · "Exif\0\0". */
const TIFF_START = 10;

const TAG_ORIENTATION = 0x0112;
const TAG_EXIF_IFD = 0x8769;
const TAG_COLOR_SPACE = 0xa001;
const TAG_PIXEL_X = 0xa002;
const TAG_PIXEL_Y = 0xa003;
const TAG_INTEROP_IFD = 0xa005;
const TAG_INTEROP_INDEX = 0x0001;

const TYPE_ASCII = 2;
const TYPE_SHORT = 3;
const TYPE_LONG = 4;

/** Một IFD thật có vài chục ô; quá mức này là dữ liệu rác. */
const MAX_IFD_ENTRIES = 512;

export type JpegSize = { width: number; height: number };

type Segment = { marker: number; start: number; end: number };

/**
 * Các đoạn đầu tệp JPEG tới trước dữ liệu ảnh (SOS). `bytes` có thể chỉ là phần
 * ĐẦU tệp: đoạn bị cắt ngang thì dừng lại, không đoán.
 */
function headerSegments(bytes: Uint8Array): Segment[] {
  const out: Segment[] = [];
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== SOI) return out;
  let offset = 2;
  while (offset + 4 <= bytes.length) {
    if (bytes[offset] !== 0xff) return out;
    let markerAt = offset + 1;
    while (bytes[markerAt] === 0xff) markerAt++; // byte đệm 0xFF
    const marker = bytes[markerAt] ?? 0;
    if (marker === SOS || marker === EOI || marker === SOI || marker === 0x00) return out;
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      offset = markerAt + 1;
      continue;
    }
    const length = ((bytes[markerAt + 1] ?? 0) << 8) | (bytes[markerAt + 2] ?? 0);
    const end = markerAt + 1 + length;
    if (length < 2 || end > bytes.length) return out;
    out.push({ marker, start: offset, end });
    offset = end;
  }
  return out;
}

function isExifSegment(bytes: Uint8Array, seg: Segment): boolean {
  return (
    seg.marker === APP1 &&
    seg.end - seg.start > TIFF_START + 8 &&
    EXIF_HEADER.every((value, index) => bytes[seg.start + 4 + index] === value)
  );
}

/** Đoạn APP1 EXIF đầu tiên (cả FF E1 + độ dài), bản sao; `null` khi không có. */
export function findExifSegment(head: Uint8Array): Uint8Array | null {
  const seg = headerSegments(head).find((s) => isExifSegment(head, s));
  return seg ? head.slice(seg.start, seg.end) : null;
}

/** Kích thước khung ảnh ghi trong đoạn SOFn — kích thước TRƯỚC khi xoay theo cờ. */
export function jpegFrameSize(head: Uint8Array): JpegSize | null {
  for (const seg of headerSegments(head)) {
    const m = seg.marker;
    // SOF0–SOF15 trừ DHT (C4), JPG (C8), DAC (CC).
    if (m < 0xc0 || m > 0xcf || m === 0xc4 || m === 0xc8 || m === 0xcc) continue;
    const p = seg.start + 4;
    if (p + 5 > seg.end) return null;
    const height = ((head[p + 1] ?? 0) << 8) | (head[p + 2] ?? 0);
    const width = ((head[p + 3] ?? 0) << 8) | (head[p + 4] ?? 0);
    return width > 0 && height > 0 ? { width, height } : null;
  }
  return null;
}

/** Bộ đọc/vá khối TIFF nằm trong đoạn APP1 — mọi lần đọc đều có rào biên. */
class Tiff {
  private readonly view: DataView;
  private readonly size: number;
  readonly le: boolean;
  readonly ok: boolean;

  constructor(readonly segment: Uint8Array) {
    this.size = Math.max(0, segment.length - TIFF_START);
    this.view = new DataView(segment.buffer, segment.byteOffset + Math.min(TIFF_START, segment.length), this.size);
    const b0 = segment[TIFF_START];
    const b1 = segment[TIFF_START + 1];
    this.le = b0 === 0x49 && b1 === 0x49;
    const be = b0 === 0x4d && b1 === 0x4d;
    this.ok = (this.le || be) && this.size >= 8 && this.u16(2) === 42;
  }

  inRange(offset: number, length: number): boolean {
    return Number.isInteger(offset) && offset >= 0 && offset + length <= this.size;
  }
  u16(offset: number): number {
    return this.view.getUint16(offset, this.le);
  }
  u32(offset: number): number {
    return this.view.getUint32(offset, this.le);
  }
  setU16(offset: number, value: number) {
    this.view.setUint16(offset, value, this.le);
  }
  setU32(offset: number, value: number) {
    this.view.setUint32(offset, value, this.le);
  }

  /** Vị trí các ô của một IFD; `null` khi IFD tràn biên. */
  entries(ifd: number): number[] | null {
    if (!this.inRange(ifd, 2)) return null;
    const count = this.u16(ifd);
    if (count > MAX_IFD_ENTRIES || !this.inRange(ifd + 2, count * 12 + 4)) return null;
    return Array.from({ length: count }, (_, i) => ifd + 2 + i * 12);
  }
  tag(entry: number): number {
    return this.u16(entry);
  }
  type(entry: number): number {
    return this.u16(entry + 2);
  }
  count(entry: number): number {
    return this.u32(entry + 4);
  }
}

/** Ô một-giá-trị SHORT/LONG; `null` khi kiểu hay số lượng không đúng chuẩn. */
function readScalar(t: Tiff, entry: number): number | null {
  if (t.count(entry) !== 1) return null;
  if (t.type(entry) === TYPE_SHORT) return t.u16(entry + 8);
  if (t.type(entry) === TYPE_LONG) return t.u32(entry + 8);
  return null;
}

function writeScalar(t: Tiff, entry: number, value: number): boolean {
  if (t.count(entry) !== 1) return false;
  if (t.type(entry) === TYPE_SHORT) {
    t.setU16(entry + 8, Math.min(value, 0xffff));
    return true;
  }
  if (t.type(entry) === TYPE_LONG) {
    t.setU32(entry + 8, value);
    return true;
  }
  return false;
}

/** Cờ xoay 1–8 của ảnh; không có / không đọc được = 1 (đã đúng chiều). */
export function readOrientation(exifSegment: Uint8Array): number {
  const t = new Tiff(exifSegment);
  if (!t.ok) return 1;
  const entries = t.entries(t.u32(4));
  const hit = entries?.find((e) => t.tag(e) === TAG_ORIENTATION);
  const value = hit === undefined ? null : readScalar(t, hit);
  return value !== null && value >= 1 && value <= 8 ? value : 1;
}

export type ExifCopyOptions = {
  /** Trình duyệt đã xoay điểm ảnh theo cờ lúc giải mã → cờ của bản nén về 1. */
  resetOrientation: boolean;
  /** Kích thước bản nén. */
  width: number;
  height: number;
};

/** Bản sao đã vá của đoạn APP1 EXIF; `null` = bỏ EXIF (khối hỏng / lạ). */
export function prepareExifForCopy(exifSegment: Uint8Array, options: ExifCopyOptions): Uint8Array | null {
  const segment = exifSegment.slice();
  if (segment.length > 0xffff + 2) return null;
  const t = new Tiff(segment);
  if (!t.ok) return null;

  const ifd0 = t.entries(t.u32(4));
  if (!ifd0) return null;
  let exifIfd: number | null = null;
  for (const e of ifd0) {
    if (t.tag(e) === TAG_ORIENTATION && options.resetOrientation && !writeScalar(t, e, 1)) return null;
    if (t.tag(e) === TAG_EXIF_IFD) exifIfd = readScalar(t, e);
  }
  if (exifIfd === null) return segment;

  const exifEntries = t.entries(exifIfd);
  if (!exifEntries) return null;
  let interopIfd: number | null = null;
  for (const e of exifEntries) {
    const tag = t.tag(e);
    if (tag === TAG_COLOR_SPACE) writeScalar(t, e, 1);
    else if (tag === TAG_PIXEL_X) writeScalar(t, e, options.width);
    else if (tag === TAG_PIXEL_Y) writeScalar(t, e, options.height);
    else if (tag === TAG_INTEROP_IFD) interopIfd = readScalar(t, e);
  }
  if (interopIfd === null || interopIfd === exifIfd) return segment;

  const interopEntries = t.entries(interopIfd);
  if (!interopEntries) return segment; // Interop hỏng không đáng bỏ cả khối
  for (const e of interopEntries) {
    // "R03" + NUL nằm gọn trong 4 byte giá trị của ô.
    if (t.tag(e) === TAG_INTEROP_INDEX && t.type(e) === TYPE_ASCII && t.count(e) === 4) {
      const at = TIFF_START + e + 8;
      if (segment[at] === 0x52 && segment[at + 1] === 0x30 && segment[at + 2] === 0x33) {
        segment[at + 1] = 0x39; // R03 → R98
        segment[at + 2] = 0x38;
      }
    }
  }
  return segment;
}

/**
 * Chèn đoạn EXIF vào một JPEG chưa có EXIF (bản MozJPEG vừa mã hoá). Đặt ngay
 * sau SOI, hoặc sau đoạn JFIF APP0 nếu tệp mở đầu bằng nó — cách phần mềm ảnh
 * vẫn làm. `null` khi đầu vào không phải JPEG.
 */
export function insertExifSegment(jpeg: Uint8Array, exifSegment: Uint8Array): Uint8Array<ArrayBuffer> | null {
  if (jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== SOI) return null;
  let at = 2;
  if (jpeg[2] === 0xff && jpeg[3] === APP0) {
    const length = ((jpeg[4] ?? 0) << 8) | (jpeg[5] ?? 0);
    if (length < 2 || 4 + length > jpeg.length) return null;
    at = 4 + length;
  }
  const out = new Uint8Array(jpeg.length + exifSegment.length);
  out.set(jpeg.subarray(0, at), 0);
  out.set(exifSegment, at);
  out.set(jpeg.subarray(at), at + exifSegment.length);
  return out;
}
